import { api, errorBanner, loadingBanner } from "../api.js";
import { replaceQuery } from "../router.js";
import { escapeHtml, currentLegislature, legislatureRange } from "../format.js";
import { renderPagination, bindPagination } from "../components/pagination.js";
import { renderHouseTabs, bindHouseTabs } from "../components/segmented.js";
import { renderFilterFooter, isDirty } from "../components/filterbar.js";
import { renderMemberCard } from "../components/membercard.js";

const UFS = "AC AL AM AP BA CE DF ES GO MA MG MS MT PA PB PE PI PR RJ RN RO RR RS SC SE SP TO".split(" ");
const DEFAULTS = { house: "", party: "", uf: "", q: "", legislature: "", sort: "name", mostrar: "" };

export async function renderMembers({ root, query, isCurrent }) {
  document.title = "Parlamentares · Como Vota";
  const state = {
    house: query.house || "",
    party: (query.party || "").toUpperCase(),
    uf: query.uf || "",
    q: query.q || "",
    legislature: query.legislature || "",
    sort: query.sort || "name",
    mostrar: query.mostrar === "todos" ? "todos" : "", // "" = em exercício
    page: Number(query.page || 1),
  };
  const leg = currentLegislature();
  root.innerHTML = `
    <section class="detail detail--wide">
      <div class="page-head">
        <span class="eyebrow-pill">Parlamentares</span>
        <h1 class="page-title">Parlamentares</h1>
        <p class="page-head__kicker" data-kicker>Em exercício</p>
        <p class="muted" data-subtitle></p>
      </div>
      ${renderHouseTabs(state.house)}
      <form class="filters" data-form>
        <div class="filters__row">
          <label>UF
            <select name="uf"><option value="">Todas as UFs</option>${UFS.map((u) => `<option ${state.uf === u ? "selected" : ""}>${u}</option>`).join("")}</select>
          </label>
          <label>Partido
            <select name="party" data-party><option value="">Todos os partidos</option>${state.party ? `<option value="${escapeHtml(state.party)}" selected>${escapeHtml(state.party)}</option>` : ""}</select>
          </label>
          <label>Situação
            <select name="mostrar">
              <option value="" ${state.mostrar === "" ? "selected" : ""}>Em exercício</option>
              <option value="todos" ${state.mostrar === "todos" ? "selected" : ""}>Todos</option>
            </select>
          </label>
          <label>Legislatura
            <select name="legislature">
              <option value="">Atual (${leg}ª)</option>
              ${[leg - 1, leg - 2, leg - 3].map((n) => `<option value="${n}" ${String(state.legislature) === String(n) ? "selected" : ""}>${n}ª</option>`).join("")}
            </select>
          </label>
          <label>Ordenar
            <select name="sort">
              <option value="name" ${state.sort === "name" ? "selected" : ""}>Nome</option>
              <option value="attendance" ${state.sort === "attendance" ? "selected" : ""}>Presença</option>
              <option value="loyalty" ${state.sort === "loyalty" ? "selected" : ""}>Fidelidade partidária</option>
              <option value="gov" ${state.sort === "gov" ? "selected" : ""}>Alinhamento com o governo</option>
            </select>
          </label>
          <label>Nome <input type="search" name="q" value="${escapeHtml(state.q)}" placeholder="Buscar nome" /></label>
        </div>
        <div class="filters__foot" data-foot></div>
      </form>
      <div data-results>${loadingBanner()}</div>
    </section>`;

  const form = root.querySelector("[data-form]");
  const foot = root.querySelector("[data-foot]");
  const results = root.querySelector("[data-results]");
  const subtitle = root.querySelector("[data-subtitle]");
  const partySelect = root.querySelector("[data-party]");

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    readForm();
    load();
  });
  form.querySelectorAll("select, input").forEach((el) =>
    el.addEventListener("change", () => {
      const cohortChanged = el.name === "legislature" || el.name === "mostrar";
      readForm();
      load();
      if (cohortChanged) loadParties();
    }),
  );
  bindHouseTabs(root, (house) => {
    readForm();
    state.house = house;
    state.page = 1;
    load();
    loadParties();
  });

  function readForm() {
    state.party = form.party.value.trim().toUpperCase();
    state.uf = form.uf.value;
    state.mostrar = form.mostrar.value === "todos" ? "todos" : "";
    state.legislature = form.legislature.value;
    state.sort = form.sort.value;
    state.q = form.q.value.trim();
    state.page = 1;
  }

  function updateSubtitle() {
    const n = state.legislature || leg;
    const who = state.house === "camara" ? "Deputados" : state.house === "senado" ? "Senadores" : "Deputados e senadores";
    root.querySelector("[data-kicker]").textContent = state.mostrar === "todos" ? "Todos os mandatos" : "Em exercício";
    subtitle.textContent = `${who} da ${n}ª legislatura (${legislatureRange(Number(n))})${state.mostrar === "todos" ? "." : " em exercício."}${
      state.house ? "" : " Ordenações por índice comparam deputados e senadores entre si."
    }`;
  }

  function footer(total, loading) {
    foot.innerHTML = renderFilterFooter({
      total,
      loading,
      noun: "parlamentar",
      nounPlural: "parlamentares",
      hasFilters: isDirty(state, DEFAULTS),
      clearHref: "/parlamentares",
    });
  }

  async function loadParties() {
    try {
      const parties = await api.parties({
        house: state.house,
        legislature: state.legislature,
        in_exercise: state.mostrar === "todos" ? "all" : "true",
      });
      if (!isCurrent()) return;
      const known = parties.some((p) => (p.sigla || "").toUpperCase() === state.party);
      partySelect.innerHTML =
        `<option value="">Todos os partidos</option>` +
        (state.party && !known ? `<option value="${escapeHtml(state.party)}" selected>${escapeHtml(state.party)}</option>` : "") +
        parties
          .map(
            (p) =>
              `<option value="${escapeHtml(p.sigla)}" ${(p.sigla || "").toUpperCase() === state.party ? "selected" : ""}>${escapeHtml(p.sigla)}${
                p.count ? ` (${p.count})` : ""
              }</option>`,
          )
          .join("");
    } catch {
      /* keep whatever the select already has */
    }
  }

  async function load() {
    replaceQuery(state);
    updateSubtitle();
    results.innerHTML = loadingBanner();
    footer(null, true);
    try {
      const page = await api.members({
        house: state.house,
        party: state.party,
        uf: state.uf,
        q: state.q,
        legislature: state.legislature,
        sort: state.sort,
        in_exercise: state.mostrar === "todos" ? "" : "true",
        page: state.page,
        page_size: 30,
      });
      if (!isCurrent()) return;
      footer(page.total, false);
      if (!page.items?.length) {
        results.innerHTML = `<div class="empty-card">Nenhum parlamentar encontrado com esses filtros.</div>`;
        return;
      }
      results.innerHTML = `<ul class="card-grid card-grid--members">${page.items.map(renderMemberCard).join("")}</ul>${renderPagination(page)}`;
      bindPagination(results, (n) => {
        state.page = n;
        load();
      });
    } catch (err) {
      if (!isCurrent()) return;
      footer(null, false);
      results.innerHTML = errorBanner(err);
    }
  }

  await Promise.all([load(), loadParties()]);
}
